import React, { useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  // IconButton,
} from '@spotoninc/nexus-react';
import { Text, Input, Box, Flex, IconButton } from '@chakra-ui/react';
import { ArrowLeftIcon } from '@spotoninc/nexus-web-assets';

/**
 * Location Selection Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Function} props.onBack - Function to call to go back to the privilege modal
 * @param {number} props.currentIntegration - The ID of the current integration
 * @param {Object} props.locations - Map of integration IDs to their locations
 * @param {Object} props.selectedLocations - Map of integration IDs to their selected location IDs
 * @param {Function} props.handleLocationChange - Function to handle location selection changes
 */
const LocationModal = ({
  isOpen,
  onClose,
  onBack,
  currentIntegration,
  locations,
  selectedLocations,
  handleLocationChange,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [clickedCheckbox, setClickedCheckbox] = useState(null);

  // Filter locations based on search term
  const filteredLocations =
    currentIntegration && locations[currentIntegration]
      ? locations[currentIntegration].filter(location =>
          location.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Flex align="center">
          <IconButton
            variant="ghost"
            aria-label="Go back"
            onClick={onBack}
            size="sm"
            borderRadius="full"
            _hover={{ bg: '#eff5ff' }}
            w="44px"
            h="44px"
            mr={6}
          >
            <ArrowLeftIcon width="24px" height="24px" />
          </IconButton>
          <Text>Assign Locations</Text>
        </Flex>
      }
      size="lg"
    >
      <ModalBody padding={0} maxHeight="700px">
        {currentIntegration && locations[currentIntegration] ? (
          <>
            <Box mb={3} minWidth={{ base: '', md: '500px' }} mx={5} pt={5}>
              <Input
                placeholder="Search locations..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                size="md"
              />
            </Box>
            <Box overflowY="auto" maxHeight="450px">
              <Table>
                <TableBody>
                  {filteredLocations.length > 0 ? (
                    filteredLocations.map(location => {
                      const locationId = location.location_id;
                      const isSelected =
                        selectedLocations[currentIntegration] &&
                        selectedLocations[currentIntegration].includes(
                          locationId
                        );

                      return (
                        <TableRow
                          key={locationId}
                          variant="bottom-divider"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            // Only toggle if the checkbox wasn't directly clicked
                            if (clickedCheckbox !== locationId) {
                              handleLocationChange(locationId, !isSelected);
                            }
                            // Reset the clicked checkbox state
                            setClickedCheckbox(null);
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Box mr={3}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    // Mark this checkbox as directly clicked
                                    setClickedCheckbox(locationId);
                                    // Toggle the selection directly
                                    handleLocationChange(
                                      locationId,
                                      !isSelected
                                    );
                                  }}
                                  onClick={e => {
                                    // Stop propagation to prevent row click
                                    e.stopPropagation();
                                  }}
                                  style={{ width: '16px', height: '16px' }}
                                />
                              </Box>
                              <Text>{location.description}</Text>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow variant="bottom-divider">
                      <TableCell colSpan={1} textAlign="center">
                        <Text py={2}>No locations match your search</Text>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        ) : (
          <Text textAlign="center" py={4}>
            No locations available for this integration
          </Text>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={onBack}>Done</Button>
      </ModalFooter>
    </Modal>
  );
};

export default LocationModal;
